import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { BASE_API, USER_TOKEN } from 'config/consts';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const getAuthHeader = () => {
  try {
    const token = localStorage.getItem(USER_TOKEN);
    return token ? `${JSON.parse(token)}` : '';
  } catch (e) {
    console.error('Erro ao parsear o token do localStorage:', e);
    return '';
  }
};

const defaultLiters = {
  gasolinaComum: 0,
  gasolinaAditivada: 0,
  diesel: 0,
  dieselS10: 0,
  etanol: 0,
  podium: 0,
};

const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => query.append(key, item));
      } else {
        query.append(key, value);
      }
    }
  });
  return query.toString();
};

const sanitizeFileName = (value, fallback = 'relatorio') => {
  return String(value || fallback)
    .replace(/[^a-zA-Z0-9_ -]/g, '')
    .trim();
};

const buildEmptyReportFromSelection = (report, filterFormato) => {
  if (filterFormato === 'Entidades') {
    return {
      id: report.id,
      action_id: report.id,
      name: report.name,
      station_name: report.station?.name || report.station_name || '',
      fuel_type: report.fuel_type,
      subscriptions: [],
      total_generated: 0,
      total_validated: 0,
      total_generated_liters: 0,
      total_validated_liters: 0,
    };
  }

  if (filterFormato === 'Postos') {
    return {
      id: report.id,
      name: report.identification || report.name || 'Posto',
      identification: report.identification || report.name || 'Posto',
      network_name: report.network_name || '',
      subscriptions: [],
      total_generated: 0,
      total_validated: 0,
      total_generated_liters: 0,
      total_validated_liters: 0,
    };
  }

  return {
    id: report.id,
    name: report.name,
    fuel_type: report.fuel_type,
    subscriptions: [],
    total_generated: 0,
    total_validated: 0,
    total_generated_liters: 0,
    total_validated_liters: 0,
  };
};

const normalizeReportsForPdf = (dataArray, selectedReports, filterFormato) => {
  const apiData = Array.isArray(dataArray?.data) ? dataArray.data : [];

  if (!Array.isArray(selectedReports) || selectedReports.length === 0) {
    return apiData;
  }

  return selectedReports.map((selectedReport) => {
    const foundReport = apiData.find((item) => {
      if (filterFormato === 'Entidades') {
        return item.id === selectedReport.id || item.action_id === selectedReport.id;
      }

      if (filterFormato === 'Postos') {
        return item.id === selectedReport.id;
      }

      return item.id === selectedReport.id || item.fuel_type === selectedReport.fuel_type;
    });

    return foundReport || buildEmptyReportFromSelection(selectedReport, filterFormato);
  });
};

const getPdfTitle = (reportData, filterFormato, dia, mes, ano) => {
  const reportName = reportData.name || reportData.identification || 'Relatório';

  if (filterFormato === 'Combustíveis') {
    return `Total Relatório ${reportName}-${dia}/${mes}/${ano}`;
  }

  const prefix = reportData.station_name || reportData.network_name || '';
  return `${prefix} Relatório ${reportName}-${dia}/${mes}/${ano}`.trim();
};

const nomeUnicoNoZip = (zip, nome) => {
  if (!zip.files[nome]) return nome;

  const ponto = nome.lastIndexOf('.');
  const base = ponto === -1 ? nome : nome.slice(0, ponto);
  const extensao = ponto === -1 ? '' : nome.slice(ponto);

  let contador = 2;
  while (zip.files[`${base} (${contador})${extensao}`]) {
    contador += 1;
  }
  return `${base} (${contador})${extensao}`;
};

const identificador = (reportData) =>
  sanitizeFileName(reportData.action_id || reportData.id || reportData.fuel_type, 'sem-id');

const getPdfFileName = (reportData, filterFormato, dia, mes, ano) => {
  const reportName = sanitizeFileName(
    reportData.name || reportData.identification || reportData.fuel_type,
    'relatorio'
  );

  if (filterFormato === 'Combustíveis') {
    return `Total_relatorio_${reportName}_${dia}_${mes}_${ano}.pdf`;
  }

  const prefix = sanitizeFileName(
    reportData.station_name || reportData.network_name || 'Relatorio',
    'Relatorio'
  );

  return `${prefix}_relatorio_${reportName}_${identificador(reportData)}_${dia}_${mes}_${ano}.pdf`;
};

const gerarExcel = async (dados, filterFormato) => {
  try {
    const zip = new JSZip();
    const dataAtual = new Date();
    const dia = String(dataAtual.getDate()).padStart(2, '0');
    const mes = String(dataAtual.getMonth() + 1).padStart(2, '0');
    const ano = dataAtual.getFullYear();

    for (let i = 0; i < dados.data.length; i += 1) {
      const itemData = dados.data[i];
      const voucherRows = (itemData.subscriptions || []).map((item) => ({
        Nome: item.user_name || '',
        Entidade: item.user_station_name || '',
        Atividade: item.action_name || '',
        Voucher: item.code || '',
        Emissão: item.createdAt || '',
        Validador: item.validator_identification || '',
        Validação: item.validation_date || '',
        Litros: item.liters_per_voucher || 0,
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(voucherRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatório');

      const excelBuffer = XLSX.write(workbook, {
        type: 'array',
        bookType: 'xlsx',
      });

      const baseName = (itemData.name || itemData.station_name || `relatorio_${i + 1}`)
        .replace(/[^a-zA-Z0-9_ -]/g, '')
        .trim();
      zip.file(
        nomeUnicoNoZip(
          zip,
          `${baseName || `relatorio_${i + 1}`}_${identificador(itemData)}_${dia}_${mes}_${ano}.xlsx`
        ),
        new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `relatorios_${filterFormato}_${dia}_${mes}_${ano}.zip`);
  } catch (error) {
    console.error('Não foi possível gerar e compactar os relatórios.', error);
  }
};

const gerarPDF = async (
  dataArray,
  filterFormato,
  filterStartDateReport,
  filterEndDateReport,
  selectedReports
) => {
  try {
    const columns = [
      { header: 'Nome', dataKey: 'user_name' },
      { header: 'Entidade', dataKey: 'user_station_name' },
      { header: 'Atividade', dataKey: 'action_name' },
      { header: 'Voucher', dataKey: 'code' },
      { header: 'Emissão', dataKey: 'createdAt' },
      { header: 'Validador', dataKey: 'validator_identification' },
      { header: 'Validação', dataKey: 'validation_date' },
    ];

    const dataAtual = new Date();
    const dia = String(dataAtual.getDate()).padStart(2, '0');
    const mes = String(dataAtual.getMonth() + 1).padStart(2, '0');
    const ano = dataAtual.getFullYear();
    const zip = new JSZip();
    const reportsToGenerate = normalizeReportsForPdf(dataArray, selectedReports, filterFormato);

    for (const activityData of reportsToGenerate) {
      const doc = new JsPDF({ orientation: 'landscape' });
      const subscriptions = activityData.subscriptions || [];
      const title = getPdfTitle(activityData, filterFormato, dia, mes, ano);

      doc.text(title, 14, 10);
      doc.autoTable({
        columns,
        body: subscriptions,
        margin: { top: 5 },
        headStyles: { fontSize: 10 },
        bodyStyles: { fontSize: 9 },
        startY: 20,
      });

      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 20;

      if (subscriptions.length === 0) {
        doc.setFontSize(10);
        doc.text('Não há informações para o relatório selecionado.', 14, finalY + 10);
      }

      const totalValidated = activityData.total_validated || 0;
      const totalGenerated = activityData.total_generated || subscriptions.length;
      const totalValidatedLiters = activityData.total_validated_liters || 0;
      const totalGeneratedLiters = activityData.total_generated_liters || 0;

      doc.setFontSize(10);
      doc.text(
        `${totalValidated} Validados: Litros ${totalValidatedLiters}\n${totalGenerated} Gerados: Litros ${totalGeneratedLiters}`,
        14,
        finalY + (subscriptions.length === 0 ? 20 : 10)
      );

      if (filterStartDateReport && filterEndDateReport) {
        const startDate = new Date(`${filterStartDateReport}T00:00:00`);
        const endDate = new Date(`${filterEndDateReport}T23:59:59`);
        doc.setFontSize(10);
        doc.text(
          `Dados referentes ao período de: ${startDate.toLocaleDateString(
            'pt-BR'
          )} 00:00:00 até ${endDate.toLocaleDateString('pt-BR')} 23:59:59`,
          14,
          finalY + (subscriptions.length === 0 ? 35 : 25)
        );
      }

      zip.file(
        nomeUnicoNoZip(zip, getPdfFileName(activityData, filterFormato, dia, mes, ano)),
        doc.output('blob')
      );
    }

    const zipFileName = `relatorios_${filterFormato}_${dia}_${mes}_${ano}.zip`;
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipFileName);
  } catch (error) {
    console.error('Não foi possível gerar os relatórios.', error);
  }
};

const gerarPDFLitros = async (dataArray, filterStartDateReport, filterEndDateReport) => {
  try {
    const columns = [
      { header: 'Gasolina Comum (L)', dataKey: 'gasolinaComum' },
      { header: 'Gasolina Aditivada (L)', dataKey: 'gasolinaAditivada' },
      { header: 'Diesel (L)', dataKey: 'diesel' },
      { header: 'Diesel S10 (L)', dataKey: 'dieselS10' },
      { header: 'Etanol (L)', dataKey: 'etanol' },
      { header: 'Podium (L)', dataKey: 'podium' },
    ];

    const dataAtual = new Date();
    const dia = String(dataAtual.getDate()).padStart(2, '0');
    const mes = String(dataAtual.getMonth() + 1).padStart(2, '0');
    const ano = dataAtual.getFullYear();
    const zip = new JSZip();

    for (const activityData of dataArray.data || []) {
      const doc = new JsPDF({ orientation: 'landscape' });
      const nomeAtividade = (activityData.name || 'relatorio')
        .replace(/[^a-zA-Z0-9_ -]/g, '')
        .trim();

      doc.text(`Relatório ${activityData.name || 'Posto'} - ${dia}/${mes}/${ano}`, 14, 10);
      doc.autoTable({
        columns,
        body: [activityData.Litros || defaultLiters],
        margin: { top: 5 },
        headStyles: { fontSize: 10 },
        bodyStyles: { fontSize: 9 },
        startY: 20,
      });

      if (filterStartDateReport && filterEndDateReport) {
        const startDate = new Date(`${filterStartDateReport}T00:00:00`);
        const endDate = new Date(`${filterEndDateReport}T23:59:59`);
        doc.setFontSize(10);
        doc.text(
          `Dados referentes ao período de: ${startDate.toLocaleDateString(
            'pt-BR'
          )} 00:00:00 até ${endDate.toLocaleDateString('pt-BR')} 23:59:59`,
          14,
          doc.lastAutoTable.finalY + 10
        );
      }

      zip.file(
        nomeUnicoNoZip(
          zip,
          `${nomeAtividade || 'relatorio'}_${identificador(activityData)}_${dia}_${mes}_${ano}.pdf`
        ),
        doc.output('blob')
      );
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `relatorios_postos_litros_${dia}_${mes}_${ano}.zip`);
  } catch (error) {
    console.error('Não foi possível gerar os relatórios de litros.', error);
  }
};

const TanqueCheioReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterEntidade, setFilterEntidade] = useState('Todas');
  const [filterFormato, setFilterFormato] = useState('Entidades');
  const [filterForma, setFilterForma] = useState('Vouchers');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStartDateReport, setFilterStartDateReport] = useState('');
  const [filterEndDateReport, setFilterEndDateReport] = useState('');
  const [filterPosto, setFilterPosto] = useState('Todas');
  const [filterText, setFilterText] = useState('');
  const [filterSelected, setFilterSelected] = useState('Todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadFormat, setDownloadFormat] = useState('PDF');
  const [entitiesOptions, setEntitiesOptions] = useState([]);
  const [networksOptions, setNetworksOptions] = useState([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);

  const paginate = useCallback((pageNumber) => setCurrentPage(pageNumber), []);
  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  }, [totalPages]);
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: getAuthHeader(),
      };

      const [entitiesResponse, postosResponse, fuelTypesResponse] = await Promise.all([
        fetch(`${BASE_API}/tanque-cheio/reports/entities`, { headers }),
        fetch(`${BASE_API}/tanque-cheio/config-postos?tipo=posto&ativo=true`, { headers }),
        fetch(`${BASE_API}/tanque-cheio/reports/fuel-types`, { headers }),
      ]);

      const [entitiesData, postosData, fuelTypesData] = await Promise.all([
        entitiesResponse.json(),
        postosResponse.json(),
        fuelTypesResponse.json(),
      ]);

      setEntitiesOptions(Array.isArray(entitiesData) ? entitiesData : []);

      const networkSet = new Set();
      (Array.isArray(postosData) ? postosData : []).forEach((posto) => {
        if (posto.nome_rede) {
          networkSet.add(posto.nome_rede);
        }
      });
      setNetworksOptions(Array.from(networkSet).sort());
      setFuelTypeOptions(Array.isArray(fuelTypesData) ? fuelTypesData : []);
    } catch (err) {
      console.error('Erro ao carregar filtros do relatório Tanque Cheio:', err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Content-Type': 'application/json',
        Authorization: getAuthHeader(),
      };

      if (filterFormato === 'Combustíveis') {
        const data = fuelTypeOptions.map((fuel) => ({
          id: fuel.id,
          name: fuel.name,
          fuel_type: fuel.fuel_type,
          selected: false,
        }));
        setReports((prevReports) =>
          data.map((newReport) => {
            const existingReport = prevReports.find((r) => r.id === newReport.id);
            return {
              ...newReport,
              selected: existingReport ? existingReport.selected : false,
            };
          })
        );
        setTotalItems(data.length);
        setTotalPages(Math.max(1, Math.ceil(data.length / itemsPerPage)));
        setLoading(false);
        return;
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      let apiUrl = '';
      if (filterFormato === 'Entidades') {
        if (filterEntidade && filterEntidade !== 'Todas') params.entidade = filterEntidade;
        if (filterStartDate) params.startDate = formatDate(filterStartDate);
        if (filterEndDate) params.endDate = formatDate(filterEndDate);
        apiUrl = `${BASE_API}/tanque-cheio/reports/activities?${buildQueryString(params)}`;
      } else if (filterFormato === 'Postos') {
        if (filterPosto && filterPosto !== 'Todas') params.rede = filterPosto;
        apiUrl = `${BASE_API}/tanque-cheio/reports/postos?${buildQueryString(params)}`;
      }

      const response = await fetch(apiUrl, { method: 'GET', headers });
      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      setReports((prevReports) =>
        (data.data || []).map((newReport) => {
          const existingReport = prevReports.find((r) => r.id === newReport.id);
          return {
            ...newReport,
            selected: existingReport ? existingReport.selected : false,
          };
        })
      );
      setTotalItems(data.totalItems || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Falha ao buscar relatórios:', err);
      setError('Não foi possível carregar os relatórios. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    filterEndDate,
    filterEntidade,
    filterFormato,
    filterPosto,
    filterStartDate,
    fuelTypeOptions,
    itemsPerPage,
  ]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterFormato, itemsPerPage]);

  const filteredReportsToDisplay = useMemo(() => {
    let filtered = reports;

    if (filterText) {
      const lowerCaseFilterText = filterText.toLowerCase();
      if (filterFormato === 'Entidades') {
        filtered = filtered.filter((report) =>
          (report.name || '').toLowerCase().includes(lowerCaseFilterText)
        );
      } else if (filterFormato === 'Postos') {
        filtered = filtered.filter((report) =>
          (report.identification || '').toLowerCase().includes(lowerCaseFilterText)
        );
      } else if (filterFormato === 'Combustíveis') {
        filtered = filtered.filter((report) =>
          (report.name || '').toLowerCase().includes(lowerCaseFilterText)
        );
      }
    }

    if (filterSelected === 'Sim') {
      filtered = filtered.filter((report) => report.selected);
    }
    if (filterSelected === 'Não') {
      filtered = filtered.filter((report) => !report.selected);
    }

    return filtered;
  }, [filterFormato, filterSelected, filterText, reports]);

  const currentItems = useMemo(() => {
    if (filterFormato === 'Combustíveis') {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredReportsToDisplay.slice(startIndex, endIndex);
    }
    return filteredReportsToDisplay;
  }, [currentPage, filterFormato, filteredReportsToDisplay, itemsPerPage]);

  const handleCheckboxChange = useCallback((id) => {
    setReports((prevReports) =>
      prevReports.map((report) =>
        report.id === id ? { ...report, selected: !report.selected } : report
      )
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const areAllVisibleSelected =
      currentItems.length > 0 && currentItems.every((item) => item.selected);
    const visibleIds = new Set(currentItems.map((item) => item.id));

    setReports((prevReports) =>
      prevReports.map((report) => {
        if (visibleIds.has(report.id)) {
          return { ...report, selected: !areAllVisibleSelected };
        }
        return report;
      })
    );
  }, [currentItems]);

  const selectedReportsForDownload = useMemo(
    () => reports.filter((report) => report.selected),
    [reports]
  );

  const handleDownload = useCallback(async () => {
    if (selectedReportsForDownload.length === 0) {
      alert('Nenhum relatório selecionado para download.');
      return;
    }

    try {
      const headers = {
        Authorization: getAuthHeader(),
      };

      const downloadParams = new URLSearchParams();
      let downloadApiUrl = '';

      if (filterFormato === 'Entidades') {
        selectedReportsForDownload.forEach((report) =>
          downloadParams.append('reportIds', report.id)
        );

        if (filterStartDateReport) {
          downloadParams.append('startDate', formatDate(filterStartDateReport));
        }

        if (filterEndDateReport) {
          downloadParams.append('endDate', formatDate(filterEndDateReport));
        }

        downloadApiUrl = `${BASE_API}/tanque-cheio/reports/download-activities?${downloadParams.toString()}`;
      } else if (filterFormato === 'Postos') {
        selectedReportsForDownload.forEach((report) =>
          downloadParams.append('reportIds', report.id)
        );

        if (filterStartDateReport) {
          downloadParams.append('startDate', formatDate(filterStartDateReport));
        }

        if (filterEndDateReport) {
          downloadParams.append('endDate', formatDate(filterEndDateReport));
        }

        downloadApiUrl = `${BASE_API}/tanque-cheio/reports/download-postos?${downloadParams.toString()}`;
      } else {
        selectedReportsForDownload.forEach((report) =>
          downloadParams.append('fuelTypes', report.fuel_type)
        );

        if (filterStartDateReport) {
          downloadParams.append('startDate', formatDate(filterStartDateReport));
        }

        if (filterEndDateReport) {
          downloadParams.append('endDate', formatDate(filterEndDateReport));
        }

        if (filterPosto && filterPosto !== 'Todas') {
          downloadParams.append('posto', filterPosto);
        }

        downloadApiUrl = `${BASE_API}/tanque-cheio/reports/download-combustiveis?${downloadParams.toString()}`;
      }

      const response = await fetch(downloadApiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();

      if (filterFormato === 'Postos' && filterForma === 'Litros' && downloadFormat === 'PDF') {
        await gerarPDFLitros(data, filterStartDateReport, filterEndDateReport);
        return;
      }

      if (downloadFormat === 'Excel') {
        await gerarExcel(data, filterFormato);
      } else {
        await gerarPDF(
          data,
          filterFormato,
          filterStartDateReport,
          filterEndDateReport,
          selectedReportsForDownload
        );
      }
    } catch (downloadError) {
      console.error('Erro no download:', downloadError);
      alert(`Erro ao tentar baixar os relatórios: ${downloadError.message || 'Tente novamente.'}`);
    }
  }, [
    downloadFormat,
    filterEndDateReport,
    filterFormato,
    filterForma,
    filterPosto,
    filterStartDateReport,
    selectedReportsForDownload,
  ]);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Download de Relatórios</h1>

      {loading && <p>Carregando relatórios...</p>}
      {error && <p style={{ color: 'red' }}>Erro: {error}</p>}

      {!loading && !error && (
        <>
          <div
            style={{
              marginBottom: '20px',
              border: '1px solid #ccc',
              padding: '15px',
              borderRadius: '8px',
            }}>
            <h2>Filtros</h2>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                marginBottom: '15px',
              }}>
              <div>
                <label htmlFor="filterFormato" style={{ display: 'block', marginBottom: '5px' }}>
                  Tipo de Relatório:
                </label>
                <select
                  id="filterFormato"
                  value={filterFormato}
                  onChange={(e) => {
                    setFilterFormato(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}>
                  <option value="Entidades">Entidades</option>
                  <option value="Postos">Postos</option>
                  <option value="Combustíveis">Combustíveis</option>
                </select>
              </div>

              {filterFormato === 'Entidades' && (
                <>
                  <div>
                    <label
                      htmlFor="filterStartDate"
                      style={{ display: 'block', marginBottom: '5px' }}>
                      Data Início:
                    </label>
                    <input
                      type="date"
                      id="filterStartDate"
                      value={filterStartDate}
                      onChange={(e) => {
                        setFilterStartDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="filterEndDate"
                      style={{ display: 'block', marginBottom: '5px' }}>
                      Data Fim:
                    </label>
                    <input
                      type="date"
                      id="filterEndDate"
                      value={filterEndDate}
                      onChange={(e) => {
                        setFilterEndDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="filterEntidade"
                      style={{ display: 'block', marginBottom: '5px' }}>
                      Entidades:
                    </label>
                    <select
                      id="filterEntidade"
                      value={filterEntidade}
                      onChange={(e) => {
                        setFilterEntidade(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}>
                      <option value="Todas">Todas</option>
                      {entitiesOptions.map((entity) => (
                        <option key={entity.id} value={entity.id}>
                          {entity.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {(filterFormato === 'Combustíveis' || filterFormato === 'Postos') && (
                <div>
                  <label htmlFor="filterPosto" style={{ display: 'block', marginBottom: '5px' }}>
                    Rede de Postos:
                  </label>
                  <select
                    id="filterPosto"
                    value={filterPosto}
                    onChange={(e) => {
                      setFilterPosto(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}>
                    <option value="Todas">Todas</option>
                    {networksOptions.map((network) => (
                      <option key={network} value={network}>
                        {network}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {filterFormato === 'Postos' && (
                <div>
                  <label htmlFor="filterForma" style={{ display: 'block', marginBottom: '5px' }}>
                    Parâmetro:
                  </label>
                  <select
                    id="filterForma"
                    value={filterForma}
                    onChange={(e) => setFilterForma(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}>
                    <option value="Vouchers">Vouchers</option>
                    <option value="Litros">Litros</option>
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="filterSelected" style={{ display: 'block', marginBottom: '5px' }}>
                  Status de Seleção:
                </label>
                <select
                  id="filterSelected"
                  value={filterSelected}
                  onChange={(e) => setFilterSelected(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}>
                  <option value="Todos">Todos</option>
                  <option value="Sim">Selecionados</option>
                  <option value="Não">Não Selecionados</option>
                </select>
              </div>
            </div>
          </div>

          <div
            style={{
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
            {(filterFormato === 'Combustíveis' || filterFormato === 'Postos') && (
              <>
                <div>
                  <label
                    htmlFor="filterStartDateReport"
                    style={{ display: 'block', marginBottom: '5px' }}>
                    Data Início:
                  </label>
                  <input
                    type="date"
                    id="filterStartDateReport"
                    value={filterStartDateReport}
                    onChange={(e) => setFilterStartDateReport(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="filterEndDateReport"
                    style={{ display: 'block', marginBottom: '5px' }}>
                    Data Fim:
                  </label>
                  <input
                    type="date"
                    id="filterEndDateReport"
                    value={filterEndDateReport}
                    onChange={(e) => setFilterEndDateReport(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="downloadFormat" style={{ display: 'block', marginBottom: '5px' }}>
                Baixar como:
              </label>
              <select
                id="downloadFormat"
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}>
                <option value="PDF">Todos Selecionados (PDF)</option>
                <option value="Excel">Todos Selecionados (Excel)</option>
              </select>
            </div>

            <div>
              <button
                onClick={handleDownload}
                disabled={selectedReportsForDownload.length === 0}
                style={{
                  padding: '10px 15px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: 'pointer',
                }}>
                Baixar Selecionados
              </button>
            </div>
          </div>

          <div
            style={{
              marginBottom: '20px',
              marginTop: '20px',
              padding: '10px',
              border: '1px solid #eee',
              borderRadius: '5px',
            }}>
            <label
              htmlFor="filterTextList"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: 'bold',
              }}>
              Filtrar por Nome na Lista (local):
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="text"
                id="filterTextList"
                placeholder="Buscar por nome na lista atual"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  width: '100%',
                  flexGrow: 1,
                }}
              />
              <button
                onClick={handleSelectAll}
                disabled={currentItems.length === 0}
                style={{
                  padding: '8px 15px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                {currentItems.length > 0 && currentItems.every((item) => item.selected)
                  ? 'Desmarcar Todos'
                  : 'Marcar Todos'}
              </button>
            </div>
          </div>

          <ul style={{ listStyle: 'none', padding: 0 }}>
            {currentItems.length > 0 ? (
              currentItems.map((report) => (
                <li
                  key={report.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '10px',
                    border: '1px solid #eee',
                    borderRadius: '5px',
                    backgroundColor: report.selected ? '#e6f7ff' : '#fff',
                  }}>
                  <input
                    type="checkbox"
                    checked={report.selected}
                    onChange={() => handleCheckboxChange(report.id)}
                    style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                  />

                  {filterFormato === 'Entidades' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.name}</strong>
                      {' - Entidade: '}
                      {report.station?.name || 'N/A'}
                      {' - Combustível: '}
                      {report.fuel_type || 'N/A'}
                      {' - Data: '}
                      {report.start
                        ? new Date(report.start).toLocaleString('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                          })
                        : 'N/A'}
                    </span>
                  )}

                  {filterFormato === 'Postos' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.identification}</strong>
                      {' - Código: '}
                      {report.code || 'N/A'}
                      {' - Rede: '}
                      {report.network_name || 'N/A'}
                      {' - Grupo: '}
                      {report.group_name || 'N/A'}
                    </span>
                  )}

                  {filterFormato === 'Combustíveis' && (
                    <span style={{ flexGrow: 1 }}>
                      <strong>{report.name}</strong>
                    </span>
                  )}
                </li>
              ))
            ) : (
              <p>Nenhum relatório encontrado com os filtros aplicados na página atual.</p>
            )}
          </ul>

          {totalPages > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
                gap: '10px',
                flexWrap: 'wrap',
              }}>
              <label htmlFor="itemsPerPageSelect" style={{ whiteSpace: 'nowrap' }}>
                Itens por página:
              </label>
              <select
                id="itemsPerPageSelect"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}>
                <option value={5}>5</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                }}>
                Anterior
              </button>

              {Array.from({ length: totalPages }).map((_, index) => {
                const pageNumber = index + 1;

                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '5px',
                        border: `1px solid ${currentPage === pageNumber ? '#007bff' : '#ccc'}`,
                        backgroundColor: currentPage === pageNumber ? '#007bff' : '#fff',
                        color: currentPage === pageNumber ? 'white' : 'black',
                        cursor: 'pointer',
                      }}>
                      {pageNumber}
                    </button>
                  );
                }

                if (
                  (pageNumber === currentPage - 3 && currentPage > 3) ||
                  (pageNumber === currentPage + 3 && currentPage < totalPages - 2)
                ) {
                  return (
                    <span key={pageNumber} style={{ margin: '0 5px' }}>
                      ...
                    </span>
                  );
                }

                return null;
              })}

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 15px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f0f0f0',
                  cursor: 'pointer',
                }}>
                Próxima
              </button>
              <span style={{ marginLeft: '10px' }}>Total de itens: {totalItems}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TanqueCheioReportList;
